import { Calculate } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Stack, Typography } from "@mui/material";
import React, { useState, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { accountApi, useFindAccountsByPeriodAllCentersQuery } from "../../../../services/accountService/account-api";
import { AccountPeriodRequest } from "../../../../services/accountService/commands/AccountPeriodRequest";
import { AccountSummary } from "../../../../services/accountService/model/AccountSummary";
import {
    loadAccounts,
    resetCalculation,
    setPhase,
    calculateBatchReimbursement
} from "../../../../services/accountService/model/account/ReimbursementCalcSlice";
import { ReimbursementCalcGrid } from "../summary/ReimbursementCalcGrid";

function ReimbursementCalcAllCentersDialog() {
    const dispatch = useAppDispatch();
    const period = useAppSelector((state) => state.accountModule.Period);
    const year = useAppSelector((state) => state.accountModule.Year);
    const [isOpen, setIsOpen] = useState(false);

    // Fetch accounts for the current period across all centers
    const { data: periodAccounts, isFetching } = useFindAccountsByPeriodAllCentersQuery(
        new AccountPeriodRequest(Number(period), Number(year)),
        { skip: !isOpen, refetchOnMountOrArgChange: true }
    );

    const { rows, phase, processedCount, successCount, warningCount, errorCount } = useAppSelector(state => state.reimbursementCalc);

    const isProcessing = phase === 'calculating';
    const isComplete = phase === 'complete';
    const totalRows = rows.length;
    const progress = totalRows > 0 ? (processedCount / totalRows) * 100 : 0;

    const handleOpen = () => {
        dispatch(resetCalculation());
        setIsOpen(true);
    };

    const handleStartCalculation = useCallback(async () => {
        if (!periodAccounts || periodAccounts.length === 0) return;

        const overallStartTime = performance.now();
        const MAX_BATCH_SIZE = 500;
        console.log(`Starting batch calculation for ${periodAccounts.length} accounts across all centers`);

        // Load accounts into the slice
        dispatch(loadAccounts({
            accounts: periodAccounts.map((acc: AccountSummary) => ({
                accountId: acc.id,
                accountNumber: acc.accountNumber
            }))
        }));

        dispatch(setPhase('calculating'));

        // Group accounts by center so contract/payer caching works better on the backend
        const accountsByCenter = new Map<string, string[]>();
        periodAccounts.forEach((acc: AccountSummary) => {
            const centerId = acc.centerId || 'unknown';
            if (!accountsByCenter.has(centerId)) {
                accountsByCenter.set(centerId, []);
            }
            accountsByCenter.get(centerId)!.push(acc.id);
        });

        // Build chunks from center-grouped accounts, respecting the batch size limit
        const chunks: string[][] = [];
        accountsByCenter.forEach((accountIds, centerId) => {
            for (let i = 0; i < accountIds.length; i += MAX_BATCH_SIZE) {
                chunks.push(accountIds.slice(i, i + MAX_BATCH_SIZE));
            }
            console.log(`Center ${centerId}: ${accountIds.length} accounts`);
        });

        console.log(`Processing ${chunks.length} batch chunk(s) across ${accountsByCenter.size} centers`);

        // Process chunks sequentially
        for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
            const chunk = chunks[chunkIndex];
            const chunkStartTime = performance.now();
            
            try {
                await dispatch(calculateBatchReimbursement({
                    accountIds: chunk
                })).unwrap();
                
                const chunkDuration = performance.now() - chunkStartTime;
                console.log(`Chunk ${chunkIndex + 1}/${chunks.length} completed in ${chunkDuration.toFixed(2)}ms`);
            } catch (error) {
                console.error(`Error processing chunk ${chunkIndex + 1}:`, error);
            }
        }

        const overallEndTime = performance.now();
        const totalDuration = overallEndTime - overallStartTime;
        console.log(`Overall batch calculation completed in ${totalDuration.toFixed(2)}ms`);

        dispatch(setPhase('complete'));
    }, [dispatch, periodAccounts]);

    const handleClose = () => {
        // Prevent closing during processing
        if (isProcessing) {
            return;
        }

        // Invalidate cache to refresh data and retrigger grid load
        dispatch(accountApi.util.invalidateTags(['AccountCenterSummary', 'AccountSummary']));

        // Reset state
        dispatch(resetCalculation());
        setIsOpen(false);
    };

    const handleDialogClose = (_event: object, reason: string) => {
        // Only allow closing via the Cancel/Close button, not backdrop or escape
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
        }
        handleClose();
    };

    return (
        <React.Fragment>
            <Button variant='outlined' color='primary' onClick={handleOpen} startIcon={<Calculate />}>
                Calculate Reimbursement
            </Button>

            <Dialog
                open={isOpen}
                onClose={handleDialogClose}
                maxWidth='md'
                fullWidth
                disableEscapeKeyDown={true}
            >
                <DialogTitle>Calculate Reimbursement - All Centers</DialogTitle>
                <DialogContent>
                    <Stack gap={2} marginTop='10px'>
                        <Alert severity='info'>
                            This will calculate expected reimbursement for all accounts across all centers for the selected period using contract data.
                            {isFetching && (
                                <Box marginTop={1} display='flex' alignItems='center' gap={1}>
                                    <CircularProgress size={16} />
                                    <Typography variant="body2">Loading accounts...</Typography>
                                </Box>
                            )}
                            {!isFetching && periodAccounts && (
                                <Box marginTop={1}>
                                    <strong>{periodAccounts.length}</strong> accounts will be processed.
                                </Box>
                            )}
                        </Alert>

                        {isProcessing && (
                            <Box>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    Processing {processedCount} of {totalRows} accounts...
                                </Typography>
                                <LinearProgress variant="determinate" value={progress} />
                            </Box>
                        )}

                        {isComplete && (
                            <Alert severity={errorCount > 0 ? "error" : warningCount > 0 ? "warning" : "success"}>
                                Calculation complete: {successCount} succeeded{warningCount > 0 && `, ${warningCount} with warnings`}{errorCount > 0 && `, ${errorCount} failed`}
                            </Alert>
                        )}

                        {rows.length > 0 && (
                            <ReimbursementCalcGrid />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleClose}
                        color='secondary'
                        disabled={isProcessing}
                    >
                        {isComplete ? 'Close' : 'Cancel'}
                    </Button>

                    {phase === 'idle' && !isFetching && periodAccounts && periodAccounts.length > 0 && (
                        <Button
                            onClick={handleStartCalculation}
                            variant='contained'
                            color='primary'
                        >
                            Start Calculation
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}

export { ReimbursementCalcAllCentersDialog };
